/**
 * The axios transport (API_ACCESS.md §3.7): the one v2 file that imports `utils/apiService`, as
 * `ordering/commit/sharedStores.ts` is the one that reaches v1's stores (audit checks P and S).
 * It is v1's instance, unchanged, asked with v2's two flags (decided 2026-10-06): `quietUi`, so the
 * interceptor skips its own toast and approval modal, and `skipGlobalLoader`, because the action
 * shows that it is busy. Authentication, `If-Modified-Since`, the offline abort, network-status
 * tracking and the forced logout behave exactly as for v1.
 *
 * What the interceptors do with an answer, on both platforms: a 2xx resolves to the body; any other
 * status rejects with axios's `response` (`{ status, data }`); a request that never got an answer
 * (no network, or the offline abort) rejects with `undefined`.
 */
import api from "../../utils/apiService";

import type { RawAnswer, Transport } from "../types";

export const axiosTransport: Transport = {
  async send({ route, url, body, query }): Promise<RawAnswer> {
    const config = {
      method: route.method,
      url,
      data: body,
      params: query,
      quietUi: true,
      skipGlobalLoader: true,
      ...(route.base === "br" ? { customUrl: true } : {}),
    };
    try {
      return { status: 200, body: await api.request(config as never) };
    } catch (rejected: unknown) {
      const response = rejected as { status?: unknown; data?: unknown } | undefined;
      return typeof response?.status === "number" ? { status: response.status, body: response.data } : { failure: "network" };
    }
  },
};
